import React from "react";
import { useHistory } from "react-router-dom";
import { load } from "recaptcha-v3";

import { trackEvent, reportError } from "@jsainsburyplc/id-ui-common";

import { passwordService } from "services/password.service";
import useCookies from "hooks/useCookies";
import { scrollToTop } from "utils/scrollToTop";
import { getForgottenPasswordOtpLink } from "utils/urls";
import { MFA_CODE_SENT } from "constants/mfaAndOtp";
import { categoryTypes, actionTypes } from "constants/tracking";
import { getBlackBoxToken } from "utils/iovation";
import { isRecapchaEnabled, getCaptchaKeyV3 } from "utils/env";

import PageFormat from "components/PageFormat";
import ForgottenPasswordForm, {
  FORGOTTEN_PASSWORD_FORM_ID,
} from "components/ForgottenPassword/ForgottenPasswordForm";
import useHideLinks from "utils/hideLinks";
import useDigitalDataTracking from "hooks/useDigitalTrack";

const trackFormSubmit = (action) => {
  trackEvent({
    category: categoryTypes.FORGOTTEN_PASSWORD,
    action: action,
    label: "Forgotten Password Form",
  });
};

const getRecaptchaToken = async () => {
  if (isRecapchaEnabled()) {
    try {
      const recaptcha = await load(getCaptchaKeyV3());
      const recaptchaToken = await recaptcha.execute("forgotpassword");
      return recaptchaToken;
    } catch (e) {
      reportError(
        `Forgotten password - error occurred during getRecaptchaToken: ${e}`
      );
    }
  } else {
    return "";
  }
};

const ForgottenPasswordPage = () => {
  const { setEmail } = useCookies();
  const history = useHistory();
  useHideLinks(false);

  useDigitalDataTracking(
    "web:groceries:authentication:forgotten password",
    "authentication",
    "authForgottenPassword",
    "view"
  );
  const handleSubmit = async ({ email }, { setSubmitting, setStatus }) => {
    setSubmitting(true);
    const ioBlackbox = getBlackBoxToken(FORGOTTEN_PASSWORD_FORM_ID);
    const recaptchaToken = await getRecaptchaToken();
    try {
      const response = await passwordService.forgotPassword({
        email,
        ioBlackbox,
        recaptchaToken,
      });
      if (response.ok) {
        setEmail(email, { path: "/" });
        setSubmitting(false);
        sessionStorage.setItem(MFA_CODE_SENT, "true");
        history.push(getForgottenPasswordOtpLink());
        trackFormSubmit(actionTypes.SUCCESS);
      } else {
        throw Error;
      }
    } catch {
      trackFormSubmit(actionTypes.ERROR);
      setStatus({ isError: true });
      setSubmitting(false);
      scrollToTop();
    }
  };

  return (
    <PageFormat>
      <div className="c-hero c-hero--center">
        <h1>Forgot your password?</h1>
        <p>
          Enter the email address you use to log in to your account to continue.
        </p>
      </div>

      <ForgottenPasswordForm onSubmit={handleSubmit} />
    </PageFormat>
  );
};

export default ForgottenPasswordPage;
